import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Crown,
  LogIn,
  LogOut,
  MessageSquareWarning,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRoundCheck,
  Users,
  XCircle,
} from 'lucide-react'
import { accountSignOut, googleSignIn } from '../data'
import { firebaseConfigured, requireAuth, requireFunctions } from '../firebase'
import './admin.css'

const ADMIN_EMAIL = 'hampusandersson.dev@gmail.com'

type PremiumInfo = {
  active: boolean
  status?: string | null
  plan?: string | null
  provider?: string | null
  validUntil?: number | null
}

type AdminUser = {
  uid: string
  email: string | null
  displayName: string | null
  disabled: boolean
  emailVerified: boolean
  providerIds: string[]
  createdAt: string | null
  lastSignInAt: string | null
  premium: PremiumInfo
  banned: boolean
}

type ReportRow = {
  id: string
  collection: 'content_reports' | 'app_feedback'
  status: string
  createdAt: number | null
  userId?: string | null
  reporterUid?: string | null
  category?: string | null
  title?: string | null
  message?: string | null
  targetType?: string | null
  targetId?: string | null
  targetOwnerUid?: string | null
  listId?: string | null
  reason?: string | null
  details?: string | null
  source?: string | null
}

type ListUsersResponse = { users: AdminUser[]; nextPageToken: string | null }
type ListReportsResponse = { reports: ReportRow[] }
type Tab = 'users' | 'reports'

const fmtDate = (value?: number | string | null) => {
  if (!value) return '—'
  const date = typeof value === 'number' ? new Date(value) : new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('sv-SE')
}

const callable = <TReq, TRes>(name: string) => httpsCallable<TReq, TRes>(requireFunctions(), name)

export default function AdminDashboard() {
  const [authLoaded, setAuthLoaded] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [tab, setTab] = useState<Tab>('users')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [reports, setReports] = useState<ReportRow[]>([])
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [actionKey, setActionKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (!firebaseConfigured) {
      setAuthLoaded(true)
      return
    }
    return onAuthStateChanged(requireAuth(), next => {
      setUser(next)
      setAuthLoaded(true)
    })
  }, [])

  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL && user.emailVerified

  const loadUsers = useCallback(async (append = false) => {
    setLoading(true)
    setError(null)
    try {
      const result = await callable<{ pageToken?: string | null; limit?: number }, ListUsersResponse>('adminListUsers')({
        pageToken: append ? nextPageToken : null,
        limit: 100,
      })
      setUsers(current => append ? [...current, ...result.data.users] : result.data.users)
      setNextPageToken(result.data.nextPageToken)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [nextPageToken])

  const loadReports = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await callable<{ limit?: number }, ListReportsResponse>('adminListReports')({ limit: 100 })
      setReports(result.data.reports)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAdmin) return
    void loadUsers(false)
    void loadReports()
  }, [isAdmin, loadReports])

  const runUserAction = async (key: string, operation: () => Promise<unknown>, success: string) => {
    setActionKey(key)
    setError(null)
    setNotice(null)
    try {
      await operation()
      setNotice(success)
      await loadUsers(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setActionKey(null)
    }
  }

  const setPremium = (target: AdminUser, active: boolean) => runUserAction(
    `${target.uid}:premium`,
    () => callable<{ uid: string; active: boolean }, unknown>('adminSetPremium')({ uid: target.uid, active }).then(() => undefined),
    active ? `Premium granted to ${target.email ?? target.uid}.` : `Premium removed from ${target.email ?? target.uid}.`,
  )

  const setBanned = (target: AdminUser, banned: boolean) => {
    if (banned && !window.confirm(`Ban ${target.email ?? target.uid}? They will be unable to sign in again until unbanned.`)) return
    void runUserAction(
      `${target.uid}:ban`,
      () => callable<{ uid: string; banned: boolean }, unknown>('adminSetUserBan')({ uid: target.uid, banned }).then(() => undefined),
      banned ? `Banned ${target.email ?? target.uid}.` : `Unbanned ${target.email ?? target.uid}.`,
    )
  }

  const sendWarning = (target: AdminUser) => {
    const message = window.prompt(`Warning message for ${target.email ?? target.uid}:`)
    if (!message?.trim()) return
    void runUserAction(
      `${target.uid}:warning`,
      () => callable<{ uid: string; message: string }, unknown>('adminSendWarning')({ uid: target.uid, message: message.trim() }).then(() => undefined),
      `Warning sent to ${target.email ?? target.uid}.`,
    )
  }

  const updateReportStatus = async (row: ReportRow, status: string) => {
    const key = `${row.collection}:${row.id}`
    setActionKey(key)
    setError(null)
    try {
      await callable<{ collection: string; id: string; status: string }, unknown>('adminUpdateReportStatus')({
        collection: row.collection,
        id: row.id,
        status,
      })
      await loadReports()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setActionKey(null)
    }
  }

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return users
    return users.filter(item =>
      item.email?.toLowerCase().includes(needle) ||
      item.displayName?.toLowerCase().includes(needle) ||
      item.uid.toLowerCase().includes(needle),
    )
  }, [query, users])

  if (!firebaseConfigured) {
    return <AdminShell><AdminState title="Firebase is not configured" text="Add the VITE_FIREBASE_* variables used by the rest of Shelfway before opening the admin dashboard." /></AdminShell>
  }

  if (!authLoaded) {
    return <AdminShell><AdminState title="Checking administrator session…" text="Please wait." /></AdminShell>
  }

  if (!user) {
    return <AdminShell>
      <div className="admin-login-card">
        <ShieldCheck size={48}/>
        <h1>SHELFWAY Admin</h1>
        <p>This area is restricted to the Shelfway administrator Google account.</p>
        <button className="admin-primary-button" onClick={() => void googleSignIn()}><LogIn size={19}/> Sign in with Google</button>
      </div>
    </AdminShell>
  }

  if (!isAdmin) {
    return <AdminShell>
      <div className="admin-login-card admin-denied">
        <Ban size={48}/>
        <h1>Access denied</h1>
        <p><strong>{user.email ?? 'This account'}</strong> is authenticated, but is not authorized to manage Shelfway.</p>
        <button className="admin-secondary-button" onClick={() => void accountSignOut()}><LogOut size={19}/> Sign out</button>
      </div>
    </AdminShell>
  }

  return <div className="admin-page">
    <header className="admin-header">
      <div>
        <span className="admin-eyebrow"><ShieldCheck size={16}/> Restricted control plane</span>
        <h1>SHELFWAY Admin</h1>
        <p>Users, Premium entitlements, moderation reports and account enforcement.</p>
      </div>
      <div className="admin-header-actions">
        <div className="admin-identity"><strong>{user.displayName || 'Administrator'}</strong><span>{user.email}</span></div>
        <button className="admin-icon-button" title="Refresh" onClick={() => void (tab === 'users' ? loadUsers(false) : loadReports())}><RefreshCw size={19}/></button>
        <button className="admin-secondary-button" onClick={() => void accountSignOut()}><LogOut size={18}/> Sign out</button>
      </div>
    </header>

    <main className="admin-main">
      <div className="admin-tabs" role="tablist">
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}><Users size={18}/> Users</button>
        <button className={tab === 'reports' ? 'active' : ''} onClick={() => setTab('reports')}><AlertTriangle size={18}/> Reports & feedback</button>
      </div>

      {error && <div className="admin-banner error"><XCircle size={18}/><span>{error}</span></div>}
      {notice && <div className="admin-banner success"><CheckCircle2 size={18}/><span>{notice}</span></div>}

      {tab === 'users' ? <section className="admin-panel">
        <div className="admin-panel-heading">
          <div><h2>Users</h2><p>{users.length} loaded Firebase Authentication accounts.</p></div>
          <label className="admin-search"><Search size={18}/><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search email, name or UID"/></label>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>User</th><th>Premium</th><th>Account</th><th>Last sign-in</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredUsers.map(item => {
                const busy = actionKey?.startsWith(`${item.uid}:`) === true
                return <tr key={item.uid}>
                  <td><div className="admin-user-cell"><strong>{item.displayName || item.email || 'Unnamed user'}</strong><span>{item.email || 'No email'}</span><code>{item.uid}</code></div></td>
                  <td>{item.premium.active
                    ? <span className="admin-status premium"><Crown size={15}/> Active · {fmtDate(item.premium.validUntil)}</span>
                    : <span className="admin-status neutral">Free</span>}
                    {(item.premium.provider || item.premium.plan) && <small className="admin-meta">{[item.premium.plan, item.premium.provider].filter(Boolean).join(' · ')}</small>}
                  </td>
                  <td>{item.banned || item.disabled
                    ? <span className="admin-status banned"><Ban size={15}/> Banned</span>
                    : <span className="admin-status ok"><UserRoundCheck size={15}/> Active</span>}</td>
                  <td>{fmtDate(item.lastSignInAt)}</td>
                  <td><div className="admin-actions">
                    <button disabled={busy} onClick={() => void setPremium(item, !item.premium.active)}>{item.premium.active ? 'Remove Premium' : 'Grant Premium'}</button>
                    <button disabled={busy} onClick={() => sendWarning(item)}><MessageSquareWarning size={15}/> Warn</button>
                    <button className={item.banned || item.disabled ? '' : 'danger'} disabled={busy || item.email?.toLowerCase() === ADMIN_EMAIL} onClick={() => setBanned(item, !(item.banned || item.disabled))}>{item.banned || item.disabled ? 'Unban' : 'Ban'}</button>
                  </div></td>
                </tr>
              })}
            </tbody>
          </table>
        </div>

        {loading && <div className="admin-loading">Loading…</div>}
        {nextPageToken && <button className="admin-secondary-button admin-load-more" disabled={loading} onClick={() => void loadUsers(true)}>Load more users</button>}
      </section> : <section className="admin-panel">
        <div className="admin-panel-heading"><div><h2>Reports & feedback</h2><p>Trusted backend view of moderation reports and app feedback.</p></div></div>
        <div className="admin-report-list">
          {reports.length === 0 && !loading && <div className="admin-empty">No reports or feedback found.</div>}
          {reports.map(row => <article className="admin-report-card" key={`${row.collection}:${row.id}`}>
            <div className="admin-report-top">
              <span className={`admin-status ${row.status === 'new' ? 'banned' : 'neutral'}`}>{row.status || 'new'}</span>
              <span>{row.collection === 'content_reports' ? 'Content report' : 'App feedback'}</span>
              <time>{fmtDate(row.createdAt)}</time>
            </div>
            <h3>{row.collection === 'content_reports' ? `${row.reason || 'Report'} · ${row.targetType || 'content'}` : row.title || row.category || 'Feedback'}</h3>
            <p>{row.collection === 'content_reports' ? row.details || 'No additional reporter details.' : row.message || 'No message'}</p>
            <div className="admin-report-meta">
              <code>{row.reporterUid || row.userId || 'unknown user'}</code>
              {row.collection === 'content_reports' && <span>Target: <code>{row.targetId || 'unknown'}</code></span>}
              {row.collection === 'content_reports' && row.listId && <span>List: <code>{row.listId}</code></span>}
              {row.source && <span>Source: {row.source}</span>}
              {row.targetOwnerUid && <span>Target owner: <code>{row.targetOwnerUid}</code></span>}
            </div>
            <div className="admin-actions">
              <button disabled={actionKey === `${row.collection}:${row.id}`} onClick={() => void updateReportStatus(row, 'reviewing')}>Reviewing</button>
              {row.collection === 'app_feedback' && <button disabled={actionKey === `${row.collection}:${row.id}`} onClick={() => void updateReportStatus(row, 'planned')}>Planned</button>}
              <button disabled={actionKey === `${row.collection}:${row.id}`} onClick={() => void updateReportStatus(row, 'resolved')}>Resolve</button>
              {row.collection === 'app_feedback' && <button disabled={actionKey === `${row.collection}:${row.id}`} onClick={() => void updateReportStatus(row, 'duplicate')}>Duplicate</button>}
              <button disabled={actionKey === `${row.collection}:${row.id}`} onClick={() => void updateReportStatus(row, row.collection === 'content_reports' ? 'dismissed' : 'closed')}>Dismiss / close</button>
            </div>
          </article>)}
        </div>
        {loading && <div className="admin-loading">Loading…</div>}
      </section>}
    </main>
  </div>
}

function AdminShell({ children }: { children: ReactNode }) {
  return <div className="admin-page admin-centered">{children}</div>
}

function AdminState({ title, text }: { title: string; text: string }) {
  return <div className="admin-login-card"><ShieldCheck size={44}/><h1>{title}</h1><p>{text}</p></div>
}
